/**
 * Top-layer bookkeeping. Sheets and modals open with dialog.showModal(), which puts them in the
 * browser's top layer above every z-index. The toast host is a manual popover, also in the top
 * layer; whenever a dialog opens it re-enters the top layer so toasts stay on top of sheets.
 * Also counts open dialogs to lock page scroll (iOS ignores the modal's own inertness for scroll).
 */

type Listener = () => void;
const listeners = new Set<Listener>();
let open = 0;
let savedOverflow = '';

export function onTopLayerChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) fn();
}

export function dialogOpened(): void {
  open += 1;
  if (open === 1 && typeof document !== 'undefined') {
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  emit();
}

export function dialogClosed(): void {
  open = Math.max(0, open - 1);
  if (open === 0 && typeof document !== 'undefined') {
    document.documentElement.style.overflow = savedOverflow;
  }
  emit();
}

export function openDialogCount(): number {
  return open;
}

export function supportsPopover(el: HTMLElement): boolean {
  return typeof el.showPopover === 'function' && typeof el.hidePopover === 'function';
}

/** Moves a manual popover to the top of the top layer (it re-enters after any open dialog). */
export function raisePopover(el: HTMLElement | null): void {
  if (!el || !supportsPopover(el)) return;
  try {
    if (el.matches(':popover-open')) el.hidePopover();
    el.showPopover();
  } catch {
    // Not connected yet, or the browser refused. The z-index fallback still applies.
  }
}
